import { isSafeBackgroundPosition, isSafeImageUrl } from "@calcom/lib/bookingBackground";
import { userMetadata } from "@calcom/prisma/zod-utils";
import classNames from "@calcom/ui/classNames";

export type BookingBackgroundSetting = { enabled: boolean; css?: string } | null;

export const getBookingBackground = (metadata: unknown): BookingBackgroundSetting =>
  userMetadata.safeParse(metadata).data?.bookingBackground ?? null;

export type ProfileBannerSetting = { url: string; position?: string } | null;

export const getProfileBanner = (metadata: unknown): ProfileBannerSetting => {
  const banner = userMetadata.safeParse(metadata).data?.profileBanner;
  if (!banner?.url || !isSafeImageUrl(banner.url)) return null;
  return {
    url: banner.url,
    ...(banner.position && isSafeBackgroundPosition(banner.position) ? { position: banner.position } : {}),
  };
};

// A light veil leaves the image visible. The shadow keeps white text legible where the veil alone gives 3.4:1
export const profileBannerStyle = (banner: NonNullable<ProfileBannerSetting>) => ({
  backgroundImage: `linear-gradient(rgb(0 0 0 / 0.45), rgb(0 0 0 / 0.45)), url("${banner.url}")`,
  backgroundSize: "cover",
  backgroundPosition: banner.position ?? "center",
  textShadow: "0 1px 3px rgb(0 0 0 / 0.6)",
});

/** Behind the page content of the public booking pages, light mode only; no setting means the built-in light rays. */
export function BookingPageBackground({ setting }: { setting: BookingBackgroundSetting }) {
  if (setting && !setting.enabled) return null;
  const css = setting?.css?.trim();
  return (
    <div
      aria-hidden
      data-testid="booking-page-background"
      className={classNames(
        "pointer-events-none fixed inset-0 -z-10 dark:hidden",
        !css && "booking-light-rays"
      )}
      style={css ? { background: css } : undefined}
    />
  );
}
